import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  CalendarDays,
  Upload,
  Users,
  Image,
  Settings,
  CreditCard,
  Shield,
  Share2,
  QrCode,
  User,
  Clock,
  Eye,
  Lock
} from 'lucide-react';

interface AccordionItemProps {
  title: string;
  icon: React.ReactNode;
  isOpen: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

const AccordionItem: React.FC<AccordionItemProps> = ({ title, icon, isOpen, onClick, children }) => {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={onClick}
        className={`w-full p-4 flex items-center justify-between text-left transition-colors ${
          isOpen ? 'bg-[#FF4444]/5' : 'bg-white hover:bg-gray-50'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-md ${isOpen ? 'bg-[#FF4444]/10 text-[#FF4444]' : 'bg-gray-100 text-gray-500'}`}>
            {icon}
          </div>
          <span className={`font-medium ${isOpen ? 'text-[#FF4444]' : 'text-gray-900'}`}>{title}</span>
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>
      {isOpen && (
        <div className="p-4 bg-white border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

const HowToUse: React.FC = () => {
  const [openSection, setOpenSection] = useState<string | null>('events');

  const toggleSection = (section: string) => {
    setOpenSection(openSection === section ? null : section);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold mb-2">Platform Documentation</h1>
        <p className="text-gray-600">
          Learn how to use our platform effectively with this comprehensive guide
        </p>
      </div>

      <div className="space-y-4">
        {/* Events Management */}
        <AccordionItem
          title="Events Management"
          icon={<CalendarDays className="w-5 h-5" />}
          isOpen={openSection === 'events'}
          onClick={() => toggleSection('events')}
        >
          <div className="space-y-4">
            <div>
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-[#FF4444]" />
                Creating an Event
              </h3>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-2 ml-6">
                <li>Click on "Create New" in the sidebar navigation</li>
                <li>Fill in essential details: event name, location, and duration</li>
                <li>Add an optional description to provide more context</li>
                <li>Choose event duration (7 days to unlimited)</li>
                <li>Configure privacy settings and guest permissions</li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <Image className="w-4 h-4 text-[#FF4444]" />
                Managing Photos
              </h3>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-2 ml-6">
                <li>Upload photos directly from your device</li>
                <li>Organize photos within events</li>
                <li>View all photos in a gallery format</li>
                <li>Download individual photos or entire albums</li>
                <li>Each upload uses one credit from your account</li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <Share2 className="w-4 h-4 text-[#FF4444]" />
                Sharing & Collaboration
              </h3>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-2 ml-6">
                <li>Share events via unique links</li>
                <li>Generate QR codes for easy access</li>
                <li>Enable guest uploads to allow contributions</li>
                <li>Control who can view and upload photos</li>
              </ul>
            </div>
          </div>
        </AccordionItem>

        {/* Account Settings */}
        <AccordionItem
          title="Account Settings"
          icon={<Settings className="w-5 h-5" />}
          isOpen={openSection === 'settings'}
          onClick={() => toggleSection('settings')}
        >
          <div className="space-y-4">
            <div>
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <User className="w-4 h-4 text-[#FF4444]" />
                Profile Management
              </h3>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-2 ml-6">
                <li>Update your full name and profile information</li>
                <li>View your registered email address</li>
                <li>Manage account preferences</li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#FF4444]" />
                Security Settings
              </h3>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-2 ml-6">
                <li>Change your account password</li>
                <li>Enable/disable two-factor authentication</li>
                <li>View active sessions and devices</li>
                <li>Manage login security preferences</li>
              </ul>
            </div>
          </div>
        </AccordionItem>

        {/* Credits & Billing */}
        <AccordionItem
          title="Credits & Billing"
          icon={<CreditCard className="w-5 h-5" />}
          isOpen={openSection === 'billing'}
          onClick={() => toggleSection('billing')}
        >
          <div className="space-y-4">
            <div>
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <Image className="w-4 h-4 text-[#FF4444]" />
                Image Credits
              </h3>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-2 ml-6">
                <li>Each photo upload consumes one credit</li>
                <li>Monitor your remaining credits</li>
                <li>Purchase additional credits as needed</li>
                <li>View credit usage history</li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-[#FF4444]" />
                Payment & Billing
              </h3>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-2 ml-6">
                <li>View available credit packages</li>
                <li>Make secure payments via Stripe</li>
                <li>Access payment history and invoices</li>
                <li>Manage billing information</li>
              </ul>
            </div>
          </div>
        </AccordionItem>

        {/* Privacy & Security */}
        <AccordionItem
          title="Privacy & Security"
          icon={<Lock className="w-5 h-5" />}
          isOpen={openSection === 'privacy'}
          onClick={() => toggleSection('privacy')}
        >
          <div className="space-y-4">
            <div>
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <Eye className="w-4 h-4 text-[#FF4444]" />
                Event Privacy
              </h3>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-2 ml-6">
                <li>Set events as public or private</li>
                <li>Add password protection to events</li>
                <li>Control guest permissions</li>
                <li>Manage who can view and upload photos</li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#FF4444]" />
                Event Duration
              </h3>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-2 ml-6">
                <li>Choose event duration (7, 14, 21, or 30 days)</li>
                <li>Option for unlimited duration</li>
                <li>Automatic archiving after expiration</li>
                <li>Extend event duration as needed</li>
              </ul>
            </div>
          </div>
        </AccordionItem>

        {/* Guest Features */}
        <AccordionItem
          title="Guest Features"
          icon={<Users className="w-5 h-5" />}
          isOpen={openSection === 'guests'}
          onClick={() => toggleSection('guests')}
        >
          <div className="space-y-4">
            <div>
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <Upload className="w-4 h-4 text-[#FF4444]" />
                Guest Uploads
              </h3>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-2 ml-6">
                <li>Enable/disable guest photo uploads</li>
                <li>No account required for guests to upload</li>
                <li>Automatic organization of guest photos</li>
                <li>Review and manage guest contributions</li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <QrCode className="w-4 h-4 text-[#FF4444]" />
                Event Access
              </h3>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-2 ml-6">
                <li>Access events via unique links</li>
                <li>Scan QR codes for quick access</li>
                <li>Enter passwords for protected events</li>
                <li>View and download shared photos</li>
              </ul>
            </div>
          </div>
        </AccordionItem>
      </div>
    </div>
  );
};

export default HowToUse;